import { useRef, useState } from "react";
import { uploadFileToVps } from "@/lib/vpsStorage";
import type { UploadFolder } from "@/lib/vpsStorage";

interface UploadFieldProps {
  label: string;
  value: string;
  onChange: (url: string) => void;
  accept?: string;
  hint?: string;
  required?: boolean;
  urlPlaceholder?: string;
  showPreview?: boolean;
  tenantId?: string;
  entityType?: string;
  entityId?: string;
  onUploading?: (uploading: boolean) => void;
  onUploadError?: (error: string | null) => void;
  /** VPS folder/category for organization */
  folder?: UploadFolder;
  /** Allowed MIME types for client-side validation. E.g. ["image/jpeg", "image/png"] */
  allowedTypes?: string[];
  /** Max file size in MB */
  maxSizeMB?: number;
  /** Optional: called with full metadata on successful upload */
  onUploaded?: (url: string, metadata: { fileId?: string; filename?: string; size?: number; mimeType?: string; path?: string }) => void;
}

export default function UploadField({
  label,
  value,
  onChange,
  accept = "*/*",
  hint,
  required = false,
  urlPlaceholder = "https://...",
  showPreview = false,
  tenantId,
  entityType,
  entityId,
  onUploading,
  onUploadError,
  folder,
  allowedTypes,
  maxSizeMB,
  onUploaded,
}: UploadFieldProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);

  const canUpload = !!tenantId && !uploading;

  const isImageUrl =
    showPreview &&
    value &&
    /\.(jpg|jpeg|png|gif|webp|svg|avif)(\?.*)?$/i.test(value);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!tenantId) {
      const msg = "Tenant ID required for upload";
      setUploadError(msg);
      onUploadError?.(msg);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setUploading(true);
    setUploadError(null);
    setUploadProgress(10);
    onUploading?.(true);
    onUploadError?.(null);

    try {
      const result = await uploadFileToVps({
        file,
        tenantId,
        entityType: entityType ?? "generic",
        entityId,
        folder,
        allowedTypes,
        maxSizeMB,
      });

      if (result.success && result.url) {
        onChange(result.url);
        setUploadProgress(100);
        onUploaded?.(result.url, {
          fileId: result.fileId,
          filename: result.filename,
          size: result.size,
          mimeType: result.mime_type,
          path: result.path,
        });
      } else {
        const msg = result.error || "Upload failed";
        setUploadError(msg);
        onUploadError?.(msg);
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Upload failed";
      setUploadError(msg);
      onUploadError?.(msg);
    } finally {
      setUploading(false);
      setUploadProgress(0);
      onUploading?.(false);
      // Reset file input so same file can be selected again
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="space-y-2">
      {/* Label */}
      <label className="block text-slate-400 text-xs uppercase tracking-wider">
        {label}
        {required && <span className="text-red-400 ml-1">*</span>}
      </label>

      {/* Upload zone */}
      <div
        className={`relative rounded-xl border-2 border-dashed transition-all ${
          uploading
            ? "border-[#C8A96E]/40 bg-[#C8A96E]/5"
            : canUpload
              ? "border-slate-200 hover:border-[#C8A96E]/40 hover:bg-[#C8A96E]/3 cursor-pointer"
              : "border-slate-200 bg-slate-50 cursor-not-allowed"
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          disabled={!canUpload}
          className="hidden"
          onChange={handleFileChange}
        />

        <div className="flex flex-col items-center gap-2.5 py-5 px-4 text-center select-none">
          {/* Icon */}
          <div
            className={`w-10 h-10 flex items-center justify-center rounded-xl transition-colors ${
              uploading
                ? "bg-[#C8A96E]/15 text-[#C8A96E]"
                : canUpload
                  ? "bg-slate-100 text-slate-400"
                  : "bg-slate-100 text-slate-400"
            }`}
          >
            <span className="w-5 h-5 flex items-center justify-center">
              {uploading ? (
                <i className="ri-loader-4-line animate-spin text-xl"></i>
              ) : (
                <i className="ri-upload-line text-xl"></i>
              )}
            </span>
          </div>

          {/* Text */}
          <div>
            <p className={`text-sm font-medium ${uploading ? "text-[#0f172a]" : canUpload ? "text-[#0f172a]" : "text-slate-400"}`}>
              {uploading ? "Uploading to server…" : "Upload file"}
            </p>
            {hint && !uploading && (
              <p className="text-slate-400 text-xs mt-0.5">{hint}</p>
            )}
            {uploading && (
              <p className="text-[#C8A96E] text-xs mt-0.5">
                {uploadProgress < 100 ? "Uploading via secure proxy…" : "Processing…"}
              </p>
            )}
          </div>

          {/* Error badge */}
          {uploadError && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200">
              <span className="w-3.5 h-3.5 flex items-center justify-center text-red-500 shrink-0">
                <i className="ri-error-warning-line text-xs"></i>
              </span>
              <p className="text-red-500 text-[11px] font-medium whitespace-nowrap">{uploadError}</p>
            </div>
          )}

          {/* Upload button */}
          {!uploading && (
            <button
              type="button"
              disabled={!canUpload}
              onClick={() => canUpload && fileInputRef.current?.click()}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold transition-all whitespace-nowrap ${
                canUpload
                  ? "bg-[#2563eb] hover:bg-blue-700 text-white cursor-pointer"
                  : "bg-slate-100 text-slate-400 cursor-not-allowed border border-slate-200"
              }`}
            >
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-upload-line text-xs"></i>
              </span>
              Choose file
            </button>
          )}

          {accept !== "*/*" && (
            <p className="text-slate-300 text-[10px]">
              {accept.replace("/*", " files")}
            </p>
          )}
        </div>
      </div>

      {/* Divider */}
      <div className="flex items-center gap-3">
        <div className="flex-1 h-px bg-slate-200" />
        <span className="text-slate-400 text-[11px] font-medium whitespace-nowrap">
          or paste URL
        </span>
        <div className="flex-1 h-px bg-slate-200" />
      </div>

      {/* URL input */}
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-400">
          <i className="ri-links-line text-sm"></i>
        </span>
        <input
          type="url"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={urlPlaceholder}
          className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-4 py-2.5 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#2563eb]/50 transition-colors"
        />
        {value && (
          <button
            type="button"
            onClick={() => onChange("")}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-slate-300 hover:text-slate-500 transition-colors cursor-pointer"
            title="Clear URL"
          >
            <i className="ri-close-line text-xs"></i>
          </button>
        )}
      </div>

      {/* Image preview */}
      {isImageUrl && (
        <div className="relative w-full h-20 rounded-lg overflow-hidden border border-slate-200 bg-slate-50">
          <img
            src={value}
            alt="Preview"
            className="w-full h-full object-contain"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = "none";
            }}
          />
          <div className="absolute bottom-1.5 right-2">
            <span className="text-[10px] text-white bg-black/50 px-1.5 py-0.5 rounded">
              Preview
            </span>
          </div>
        </div>
      )}
    </div>
  );
}